import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/Button";
import { useCurrentUser } from "@/features/auth/hooks";
import { useChangePassword, useUpdateProfile } from "@/features/users/hooks";
import type { ProfileInput } from "@/features/users/types";

export function ProfilePage() {
  const currentUser = useCurrentUser();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "password" ? "password" : "profile";
  const [tab, setTab] = useState<"profile" | "password">(initialTab);
  const [profile, setProfile] = useState<ProfileInput>({ firstName: "", lastName: "", username: "", email: "" });
  const [passwords, setPasswords] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!currentUser.data) return;
    setProfile({
      firstName: currentUser.data.firstName,
      lastName: currentUser.data.lastName,
      username: currentUser.data.username,
      email: currentUser.data.email,
    });
  }, [currentUser.data]);

  function validPassword(password: string) {
    return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
  }

  function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    updateProfile.mutate(profile, {
      onSuccess: () => setMessage({ tone: "success", text: "Profile saved." }),
      onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to save profile." }),
    });
  }

  function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    if (passwords.newPassword !== passwords.confirmPassword) return setMessage({ tone: "error", text: "Passwords do not match." });
    if (!validPassword(passwords.newPassword)) return setMessage({ tone: "error", text: "Password must be 8+ chars with uppercase, lowercase, and number." });
    changePassword.mutate(
      { currentPassword: passwords.currentPassword, newPassword: passwords.newPassword },
      {
        onSuccess: () => {
          setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
          setMessage({ tone: "success", text: "Password changed." });
        },
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to change password." }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader title="My Profile" description="Update your staff profile and password." />
      <div className="flex gap-2">
        <Button variant={tab === "profile" ? "secondary" : "ghost"} onClick={() => setTab("profile")}>Profile</Button>
        <Button variant={tab === "password" ? "secondary" : "ghost"} onClick={() => setTab("password")}>Change Password</Button>
      </div>
      {tab === "profile" ? (
        <form className="space-y-5 rounded-md border border-cyan-300/15 bg-black/25 p-4" onSubmit={saveProfile}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="First Name" value={profile.firstName} onChange={(value) => setProfile((current) => ({ ...current, firstName: value }))} required />
            <TextField label="Last Name" value={profile.lastName} onChange={(value) => setProfile((current) => ({ ...current, lastName: value }))} required />
          </div>
          <TextField label="Username" value={profile.username} onChange={(value) => setProfile((current) => ({ ...current, username: value }))} required />
          <TextField label="Email" type="email" value={profile.email} onChange={(value) => setProfile((current) => ({ ...current, email: value }))} required />
          <p className="text-xs text-content-muted">Avatar upload is reserved for a future media pass.</p>
          <Message message={message} />
          <Button type="submit" disabled={updateProfile.isPending}>{updateProfile.isPending ? "Saving..." : "Save Profile"}</Button>
        </form>
      ) : (
        <form className="space-y-5 rounded-md border border-cyan-300/15 bg-black/25 p-4" onSubmit={savePassword}>
          <TextField label="Current Password" type="password" value={passwords.currentPassword} onChange={(value) => setPasswords((current) => ({ ...current, currentPassword: value }))} required />
          <TextField label="New Password" type="password" value={passwords.newPassword} onChange={(value) => setPasswords((current) => ({ ...current, newPassword: value }))} required />
          <TextField label="Confirm Password" type="password" value={passwords.confirmPassword} onChange={(value) => setPasswords((current) => ({ ...current, confirmPassword: value }))} required />
          <Message message={message} />
          <Button type="submit" disabled={changePassword.isPending}>{changePassword.isPending ? "Saving..." : "Change Password"}</Button>
        </form>
      )}
    </div>
  );
}

function Message({ message }: { message: { tone: "success" | "error"; text: string } | null }) {
  if (!message) return null;
  return <p className={message.tone === "success" ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100" : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"}>{message.text}</p>;
}

function TextField({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (value: string) => void; type?: "text" | "email" | "password"; required?: boolean }) {
  return <label className="block"><span className="text-sm text-content-secondary">{label}</span><input className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} type={type} required={required} /></label>;
}
